import { Button, TextField } from '@mgh-app/component-library';
import { useAuth } from 'auth/useAuth';
import { ValidationErrors } from 'components/ValidationErrors';
import { MainLayoutContext } from 'layouts/MainLayout';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import OtpInput from 'react-otp-input';
import { useParams } from 'react-router';
import { ResetPasswordError, useLazySetPasswordQuery } from 'services/UsersService';
import {
  borders,
  display,
  flexDirection,
  fontSize,
  fontWeight,
  gap,
  justifyContent,
  margin,
  outlineWidth,
  twCls,
} from 'style';

interface Fields {
  password: string;
  confirmPassword: string;
  otp: string;
}

type ErrorTypes = 'required' | 'pattern';

const ErrorHelper = {
  password: {
    required: 'Password is required',
  },
  confirmPassword: {
    required: 'Confirm Password is required',
    pattern: '',
    validate: 'Password and Confirm Password should match',
  },
};

export const ResetPassword = (): JSX.Element => {
  const { setTitle, showNavigationButtons, showUserProfile } = useContext(MainLayoutContext);
  const { handleSubmit, control, getValues } = useForm<Fields>({
    defaultValues: {
      password: '',
      confirmPassword: '',
      otp: '',
    },
  });
  const { email } = useParams();
  const { signIn } = useAuth();

  const [customError, setCustomError] = useState<string[]>();
  const [otp, setOtp] = useState<string>('');

  const [setPassword] = useLazySetPasswordQuery();
  const { navigateHome } = useAppNavigation();

  const changePassword = async ({ password, confirmPassword }: Fields) => {
    setCustomError([]);
    try {
      await setPassword({
        email: email || '',
        password,
        confirm_password: confirmPassword,
        verification_code: otp,
      }).unwrap();
      await signIn(email || '', password);
      navigateHome();
    } catch (err) {
      const error = err as ResetPasswordError;
      if (error.message) {
        if (Array.isArray(error?.message)) {
          setCustomError(error.message);
        } else if (error?.message) {
          setCustomError([error.message]);
        } else {
          setCustomError(['Unknown error']);
        }
      } else {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    setTitle('Reset Password');
    showNavigationButtons(true);
    showUserProfile(false);
  }, [setTitle, showNavigationButtons, showUserProfile]);

  return (
    <div className={twCls(display('flex'), flexDirection('flex-col'), margin('mt-10'))}>
      <Controller
        name="password"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={error?.type === 'required' && ErrorHelper.password.required}
            className={twCls(margin('mt-11'))}
            label="Password"
            type="password"
            {...field}
          />
        )}
      />
      <Controller
        name="confirmPassword"
        control={control}
        rules={{
          required: true,
          validate: (value) => value === getValues().password,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={ErrorHelper.confirmPassword[error?.type as ErrorTypes]}
            className={twCls(margin('mt-11'))}
            label="Confirm Password"
            type="password"
            {...field}
          />
        )}
      />
      <p className={twCls(margin('mt-16'))}>
        A six digit verification code has been sent to your email address
      </p>
      <OtpInput
        value={otp}
        onChange={setOtp}
        containerStyle={twCls(
          margin('mt-4'),
          display('flex'),
          justifyContent('justify-center'),
          fontSize('text-3xl'),
          fontWeight('font-medium'),
          gap('gap-4'),
        )}
        numInputs={6}
        isInputNum
        inputStyle={twCls(borders('border-b-2'))}
        focusStyle={twCls(outlineWidth('outline-0'))}
      />
      <ValidationErrors className={twCls(margin('mt-10'))} errors={customError} />
      <Button className={twCls(margin('mt-4'))} onClick={handleSubmit(changePassword)}>
        Continue
      </Button>
    </div>
  );
};
